import { useState, useEffect } from "react";
import { useClientRegister } from "contexts/register/ClientContext";

import { IClient } from "interfaces/Client";
import { IExpressValidatorError } from "interfaces/ExpressError";

import {
  handleClientRegisterEdit,
  handleClientRegisterSubmit,
} from "services/handles/ClientHandles";

import { StepModalTemplate } from "templates/StepModalTemplate";

import { transformValidatorError } from "utils/transformValidatorError";
import { ClientForm } from "../../../Forms/Client/ClientForm";
import { ProductForm } from "../../../Forms/Client/ProductForm";
import { RegionForm } from "../../../Forms/Client/RegionForm";

import { Tab } from "../../../TabHeader";

import * as S from "./styles";

type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  clientData?: IClient;
};

const tabs: Tab[] = [
  { index: 0, text: "Dados do cliente" },
  { index: 1, text: "Produto" },
  { index: 2, text: "Valores por região" },
];

function StepRegisterClient({ opened, setOpened, clientData }: Props) {
  const {
    client,
    product,
    regions,
    // user,
    productRegions,
    selectedServices,
    setErrors,
    errors,
    resetClient,
    searching,
    storeClient,
  } = useClientRegister();

  const [tab, setTab] = useState<Tab>(tabs[0]);
  const [invalidTabs, setInvalidTabs] = useState<Tab[]>([]);

  const [sending, setSending] = useState<boolean>(false);
  useEffect(() => {
    if (clientData) {
      setInvalidTabs([]);
      setErrors({});
      storeClient(clientData);

      return;
    }
    resetClient();
  }, [clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit() {
    setSending(true);
    if (clientData) {
      const servicesIds = selectedServices.map((service) => service.id);
      // delete client.user;
      // delete user.type;
      delete client?.product?.services;
      delete client?.product?.stringRegions;
      const { product, ...clientWithoutProduct } = client;
      return await handleClientRegisterEdit({
        // user,
        clientData: clientWithoutProduct,
        regions,
        productRegions,
        servicesIds,
        product: client.product,
      })
        .then((res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do Cliente foram cadastrados corretamente."
            );
            window.location.reload();
          }

          if (res.data.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
        })
        .catch((error) => {
          treatValidatorErrors(error.data);
        })
        .finally(() => {
          setSending(false);
        });
    }

    if (client && product && regions && selectedServices) {
      const servicesIds = selectedServices.map((service) => service.id);

      return await handleClientRegisterSubmit({
        name: client.name,
        productName: product.name,
        cnpj: client.cnpj,
        managerName: client.managerName,
        tradingName: client.tradingName,
        email: client.email,
        details: product.details,
        type: product.type,
        regions,
        productRegions,
        minimumBilling: client.minimumBilling ? client.minimumBilling : 0,
        servicesIds,
      })
        .then((res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do cliente foram cadastrados corretamente."
            );
            window.location.reload();
          }
          if (res.data.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
        })
        .catch((error) => {
          treatValidatorErrors(error.data);
        })
        .finally(() => {
          setSending(false);
        });
    }

    // setSending(true);
    setSending(false);
  }

  function setTabAsInvalid(tab: Tab) {
    setInvalidTabs((list) => [...list, tab]);
  }

  function treatValidatorErrors(validatorErrors: IExpressValidatorError) {
    setInvalidTabs([]);

    const { errors, invalidFormTypes } =
      transformValidatorError(validatorErrors);

    for (const type of invalidFormTypes) {
      if (type.includes("client")) setTabAsInvalid(tabs[0]);
      if (type.includes("product")) setTabAsInvalid(tabs[1]);
    }

    setErrors(errors);
  }

  function treatDuplicatedValidator(data: {
    [key: string]: { message: string; value: any };
  }) {
    let response: any = {};
    for (const key in data) {
      if (key.includes("cnpj")) setTabAsInvalid(tabs[0]);
      if (key.includes("cpf")) setTabAsInvalid(tabs[1]);
      if (key.includes("rg")) setTabAsInvalid(tabs[1]);
      response[key] = data[key].message;
    }
    setErrors((err) => {
      return { ...err, ...response };
    });
  }

  function onTabClick(_tab: Tab) {
    setTab(_tab);
  }

  useEffect(() => {
    if (!Object.keys(errors).some((key) => key.includes("client"))) {
      setInvalidTabs((currentInvalidTabs) =>
        currentInvalidTabs.filter((tab) => tab.index !== 0)
      );
    }
    if (
      !Object.keys(errors).some(
        (key) => key.includes("product") || key.includes("servicesIds")
      )
    ) {
      setInvalidTabs((currentInvalidTabs) =>
        currentInvalidTabs.filter((tab) => tab.index !== 1)
      );
    }
  }, [errors]);

  return (
    <StepModalTemplate
      deactivatedTabs={[]}
      title={`${clientData ? "Editar" : "Cadastrar"} cliente`}
      buttonText={`${clientData ? "Salvar" : "Efetuar cadastro"}`}
      opened={opened}
      setOpened={setOpened}
      disableButton={Object.keys(errors).length > 0}
      tabs={tabs}
      invalidTabs={invalidTabs}
      selectedTab={tab}
      sending={sending}
      setSelectedTab={onTabClick}
      handleSubmit={handleSubmit}
    >
      <S.Container>
        {searching && (
          <S.LoadingWrapper>
            <span>Buscando dados do cliente...</span>
          </S.LoadingWrapper>
        )}

        {!searching && (
          <div>
            {tab.index === 0 && <ClientForm />}

            {tab.index === 1 && <ProductForm />}

            {tab.index === 2 && <RegionForm />}
          </div>
        )}
      </S.Container>
    </StepModalTemplate>
  );
}

export { StepRegisterClient };
